import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Link as LinkIcon,
  LinkOff,
} from '@mui/icons-material';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
}

const RichTextEditor = ({ content, onChange }: RichTextEditorProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  if (!editor) {
    return null;
  }

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL:', previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const handleBulletList = () => {
    if (!editor.isActive('bulletList')) {
      // Si no estamos en una lista, creamos una nueva con un punto
      editor.chain()
        .focus()
        .toggleBulletList()
        .insertContent('• ')
        .run();
    } else {
      // Si ya estamos en una lista, solo la desactivamos
      editor.chain()
        .focus()
        .toggleBulletList()
        .run();
    }
  };

  const handleOrderedList = () => {
    if (!editor.isActive('orderedList')) {
      // Si no estamos en una lista numerada, creamos una nueva con "1. "
      editor.chain()
        .focus()
        .toggleOrderedList()
        .insertContent('1. ')
        .run();
    } else {
      // Si ya estamos en una lista numerada, solo la desactivamos
      editor.chain()
        .focus()
        .toggleOrderedList()
        .run();
    }
  };

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
        <ToggleButtonGroup size="small" sx={{ mr: 1 }}>
          <ToggleButton
            value="bold"
            selected={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <FormatBold />
          </ToggleButton>
          <ToggleButton
            value="italic"
            selected={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <FormatItalic />
          </ToggleButton>
          <ToggleButton
            value="underline"
            selected={editor.isActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <FormatUnderlined />
          </ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup size="small" sx={{ mr: 1 }}>
          <ToggleButton
            value="bulletList"
            selected={editor.isActive('bulletList')}
            onClick={handleBulletList}
          >
            <FormatListBulleted />
          </ToggleButton>
          <ToggleButton
            value="orderedList"
            selected={editor.isActive('orderedList')}
            onClick={handleOrderedList}
          >
            <FormatListNumbered />
          </ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup size="small" sx={{ mr: 1 }}>
          <ToggleButton
            value="left"
            selected={editor.isActive({ textAlign: 'left' })}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
          >
            <FormatAlignLeft />
          </ToggleButton>
          <ToggleButton
            value="center"
            selected={editor.isActive({ textAlign: 'center' })}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
          >
            <FormatAlignCenter />
          </ToggleButton>
          <ToggleButton
            value="right"
            selected={editor.isActive({ textAlign: 'right' })}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
          >
            <FormatAlignRight />
          </ToggleButton>
        </ToggleButtonGroup>

        <Tooltip title="Añadir enlace">
          <IconButton size="small" onClick={setLink}>
            <LinkIcon />
          </IconButton>
        </Tooltip>
        {editor.isActive('link') && (
          <Tooltip title="Quitar enlace">
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().unsetLink().run()}
            >
              <LinkOff />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box sx={{ p: 2 }}>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default RichTextEditor;